/**
 * Map Helper Components - Small utility components for Leaflet map management
 *
 * These components are used inside MapContainer to handle visibility,
 * bounds fitting, map ref capture, and zoom synchronization.
 */

import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

/**
 * MapVisibilityHandler - Handles map visibility changes and container resizes
 * Ensures map tiles render correctly when container size changes or becomes visible
 */
export function MapVisibilityHandler({ isVisible }: { isVisible: boolean }): null {
  const map = useMap();
  const wasVisible = useRef(false); // Start as false to trigger initial invalidate
  const containerRef = useRef<HTMLElement | null>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSizeRef = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    // Get the map container
    containerRef.current = map.getContainer();

    // Always invalidate on mount to ensure proper sizing
    setTimeout(() => {
      map.invalidateSize();
    }, 100);

    // Also invalidate after a longer delay for fullscreen/layout changes
    setTimeout(() => {
      map.invalidateSize();
    }, 500);

    // Set up ResizeObserver with debouncing to prevent excessive invalidateSize calls
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;

      // Skip if size hasn't actually changed (avoids redundant calls)
      if (lastSizeRef.current &&
          Math.abs(lastSizeRef.current.width - width) < 1 &&
          Math.abs(lastSizeRef.current.height - height) < 1) {
        return;
      }

      lastSizeRef.current = { width, height };

      // Debounce: only invalidate after resize settles
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        map.invalidateSize();
      }, 150);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also listen for window resize events (debounced)
    let windowResizeTimeout: NodeJS.Timeout | null = null;
    const handleResize = () => {
      if (windowResizeTimeout) clearTimeout(windowResizeTimeout);
      windowResizeTimeout = setTimeout(() => {
        map.invalidateSize();
      }, 150);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      if (windowResizeTimeout) clearTimeout(windowResizeTimeout);
    };
  }, [map]);

  useEffect(() => {
    // If map becomes visible after being hidden, invalidate size
    if (isVisible && !wasVisible.current) {
      setTimeout(() => {
        map.invalidateSize();
      }, 50);
    }
    wasVisible.current = isVisible;
  }, [map, isVisible]);

  return null;
}

/**
 * FitBounds - Fits map to bounds on initial load
 * Only runs once when visible and shouldFit is true
 */
export interface FitBoundsProps {
  bounds: any;
  shouldFit: boolean;
  isVisible: boolean;
  onFitted: () => void;
}

export function FitBounds({ bounds, shouldFit, isVisible, onFitted }: FitBoundsProps): null {
  const map = useMap();
  const hasRun = useRef(false);

  useEffect(() => {
    // Only fit bounds once, when visible, and only if shouldFit is true
    if (bounds && shouldFit && isVisible && !hasRun.current) {
      map.fitBounds(bounds);
      hasRun.current = true;
      onFitted();
    }
  }, [map, bounds, shouldFit, isVisible, onFitted]);

  return null;
}

/**
 * MapRefCapture - Captures Leaflet map instance to a ref
 * Useful for accessing map instance from parent component
 */
export interface MapRefCaptureProps {
  mapRef: React.MutableRefObject<any>;
}

export function MapRefCapture({ mapRef }: MapRefCaptureProps): null {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
}

/**
 * ZoomSync - Syncs zoom level from parent component
 * Only updates if difference is significant (> 0.5 zoom levels)
 */
export interface ZoomSyncProps {
  zoom: number | undefined;
}

export function ZoomSync({ zoom }: ZoomSyncProps): null {
  const map = useMap();
  useEffect(() => {
    if (zoom !== undefined && map) {
      const currentZoom = map.getZoom();
      if (Math.abs(currentZoom - zoom) > 0.5) { // Only update if difference is significant
        map.setZoom(zoom, { animate: true });
      }
    }
  }, [zoom, map]);
  return null;
}
